import { Box, Card, CardContent, Typography } from '@mui/material';
import ImageCarousel from './ImageCarousel';
import type { CarouselImage } from './productData';

export interface ProductCardProps {
  images: CarouselImage[];
  title: string;
  description: string;
  options: string[];
  dimensions: { length: string; width: string; height: string };
}

const ProductCard = ({
  images,
  title,
  description,
  options,
  dimensions,
}: ProductCardProps) => {
  return (
    <Card
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden',
        borderRadius: 0,
        border: '1px solid #ffffff',
        backgroundColor: '#1b1b1b',
      }}
    >
      <Box
        component="aside"
        sx={{
          width: { xs: '100%', md: '45%' },
          minHeight: { xs: 280, md: 320 },
          height: { xs: 280, md: 320 },
          flexShrink: 0,
          '& .ImageCarousel-root': { height: '100%' },
        }}
      >
        <ImageCarousel
          images={images}
          ariaLabel={`${title} - product images`}
          aspectRatio="4/3"
        />
      </Box>
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 3,
          px: { xs: 2, sm: 3 },
          '&:last-child': { pb: 3 },
        }}
      >
        <Typography
          component="h2"
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#ffffff',
            fontSize: { xs: '1rem', sm: '1.1rem' },
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography
          component="p"
          variant="body2"
          sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}
        >
          {description}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, listStyle: 'none' }}>
          {options.map((option, index) => (
            <Typography
              component="li"
              key={index}
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.85)',
                mb: 0.5,
                '&::marker': { content: 'none' },
              }}
            >
              {option}
            </Typography>
          ))}
        </Box>
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #ffffff' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 0.25 }}>
            {dimensions.length}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 0.25 }}>
            {dimensions.width}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            {dimensions.height}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
